import { cn } from '@/lib/utils';
import { readableForeground } from '@/lib/design/brand-colors';
import { resolveJobTypeIcon } from '@/lib/design/job-type-appearance';

const SIZES = {
  sm: { box: 'h-7 w-7 rounded-md', icon: 'h-4 w-4' },
  md: { box: 'h-9 w-9 rounded-md', icon: 'h-4 w-4' },
  lg: { box: 'h-10 w-10 rounded-md', icon: 'h-4 w-4' },
  xl: { box: 'h-14 w-14 rounded-xl', icon: 'h-5 w-5' },
} as const;

interface JobTypeAppearanceTileProps {
  color?: string | null;
  icon?: string | null;
  size?: keyof typeof SIZES;
  className?: string;
}

/**
 * The job type's Appearance (colour + icon) as one tile — the canonical way to
 * paint a job type anywhere in the app.
 *
 * The ink is derived from the surface with `readableForeground` rather than
 * hardcoded: the selectable palette is pastel, so a fixed white icon washes out
 * to invisible on every colour a user can actually pick.
 */
export function JobTypeAppearanceTile({
  color,
  icon,
  size = 'md',
  className,
}: JobTypeAppearanceTileProps) {
  const surface = color?.trim() || '#D9E5F2';
  const Icon = resolveJobTypeIcon(icon);
  const sizing = SIZES[size];

  return (
    <span
      className={cn('grid shrink-0 place-items-center border', sizing.box, className)}
      style={{ backgroundColor: surface, color: readableForeground(surface) }}
    >
      <Icon className={sizing.icon} aria-hidden="true" />
    </span>
  );
}
